// Player-facing "what's new" entries, newest first. Shown on login when the
// player's last-seen version differs from the current one. Kept short and in the
// player's voice (not the technical CHANGELOG).

export interface NewsEntry {
  version: string;
  /** Turkish master copy (the original release-note voice). */
  items: string[];
  /** English copy shown to every non-Turkish locale (the app's en-fallback
   *  convention). Older entries without it fall back to the Turkish master. */
  itemsEn?: string[];
}

export const NEWS: NewsEntry[] = [
  {
    version: '0.19.0',
    items: [
      'Çağlar artık otağdan yayılan bir dalgayla geliyor: alacakaranlık, toz ve kilim desenli bir başlık yeni dönemi karşılıyor. Dünyaya dokunarak geçişi tamamlayabilirsin.',
      'Halk sokaklarda dolaşıyor, kapılarda soluklanıyor ve inşaata yardım ediyor. Akınlarda otağa sığınıp tehlike geçince geri dönüyor.',
      'Kaynaklar artık daha az ve rastgele: topladığın kaynak daha uzun, rastgele bir sürede ve haritada başka bir yerde yeniden çıkıyor.',
      'Yerleşimin ve kayıtlı ilerlemen aynı kalıyor.',
    ],
    itemsEn: [
      'Ages now arrive in a wave from the keep, with dusk, dust and a woven title card. Tap the world to finish the transition.',
      'Citizens stroll the streets, pause at doors and help with construction. They shelter at the keep during raids and return afterward.',
      'Resources are scarcer and wander: a gathered node regrows after a longer, random wait somewhere else on the map.',
      'Your settlement layout and saved progress stay the same.',
    ],
  },
  {
    version: '0.18.0',
    items: [
      'Her çağ artık kendi görünümüne sahip: binalar, insanlar, ordular ve çevre çağ atladığında birlikte değişiyor.',
      'Gezegen çağından sonraki medeniyetler yeni mimariler, farklı araçlar ve kendilerine özgü animasyonlarla birbirinden ayrılıyor.',
      'Piksel sanatına daha zengin yüzey detayları ve çağa uygun savaş efektleri eklendi. Mevcut ilerlemen korunuyor.',
    ],
    itemsEn: [
      'Every age now has its own look: buildings, people, armies and surroundings change together as your civilization advances.',
      'Civilizations beyond the Planet Age have distinct architecture, vehicles and animations.',
      'Pixel art now has richer surface details and age-appropriate battle effects. Your existing progress is preserved.',
    ],
  },
  {
    version: '0.16.1',
    items: [
      'Artık nereye dokunacağın belli: ağaçların, taşların ve madenlerin altında kendi renginde bir halka var. Halkalar oto-toplama açılınca kayboluyor, çünkü o noktadan sonra aramak senin işin değil.',
      'Sarı hasat çemberi oyunun ilk anından beri ekranda; eskiden ilk dokunuşa kadar görünmüyordu. Boş çimende dururken de belli oluyor: çember bir şeyin üstündeyse parlıyor, değilse sönük duruyor.',
    ],
    itemsEn: [
      'You can see where to harvest now: every live tree, rock and ore vein sits on a ring in its own colour. The rings disappear once auto-collect unlocks, because finding nodes stops being your job at that point.',
      'The yellow harvest circle is on screen from the first moment instead of appearing after your first tap, and it now clearly brightens when it is actually over something.',
    ],
  },
  {
    version: '0.16.0',
    items: [
      'Artık hesap açmadan oynayabilirsin: giriş ekranında "Önce oyna, sonra giriş yap" de, doğrudan bozkıra düş. İlerlemen cihazında durur; istediğin an 👤 menüsünden giriş yapıp buluta taşırsın, oynadığın oyun kaybolmaz.',
      'Telefonda bir sürü küçük hata düzeldi: dalga panelinde güç satırı artık birbirine girmiyor, değişiklik defteri düğmesi yazının üstünü kapatmıyor, ölüm ekranı taşmıyor ve "Medeniyeti bitir" ilk oyununda karşına çıkmıyor.',
    ],
    itemsEn: [
      'You can play without an account now: tap "Play now, sign in later" and drop straight into the steppe. Your progress stays on your device, and signing in later from 👤 keeps the run you just played.',
      'A pile of small phone fixes: the power line in the wave panel no longer runs together, the changelog button no longer covers the text, the death screen no longer overflows, and "End civilization" no longer greets you on your first run.',
    ],
  },
  {
    version: '0.15.3',
    items: [
      'İlk çağ kapısındaki boss yumuşadı. Ömründe gördüğün ilk boss artık seni beş kez üst üste kesmiyor; ondan sonraki her kapı eskisi kadar zorlu kalıyor.',
    ],
    itemsEn: [
      'The boss at the first age gate hits softer. The first boss you ever meet no longer ends five runs in a row; every gate after it stays exactly as hard as before.',
    ],
  },
  {
    version: '0.15.0',
    items: [
      'Destek Ol butonu geldi: hesap panelinden oyunu (ve tüm Cryme yapımlarını) destekleyebilirsin. Oyun ücretsiz kalıyor; desteğin yeni yapımlara vakit demek. 🙏',
    ],
    itemsEn: [
      'Support button added: from the account panel you can support the game (and all Cryme builds). The game stays free; your support means time for the next build. 🙏',
    ],
  },
  {
    version: '0.14.1',
    items: [
      'Yerelleştirme düzeltmesi: düşman adları, boss adları, çağ adları ve yerleşim rütbeleri artık oyunun diline çevriliyor. Diğer dillerde Türkçe metin kalmadı.',
    ],
    itemsEn: [
      'Localization fix: enemy names, boss names, age names, and settlement ranks now translate to the game language. No more Turkish text leaking into English, German, Spanish, or French.',
    ],
  },
  {
    version: '0.14.0',
    items: [
      'Savaş artık okunuyor: dalga panelinde kendi gücünün yanında düşman gücü ve kazanır/kaybeder işareti (✓ ⚠ ✕) görünüyor. Bir zaferi neden kazandığını artık görebilirsin.',
      'Her çağa bir hikâye: yeni bir çağa ulaştığında o çağın anlatısı ekranda beliriyor, bozkırın tozundan yıldızlara uzanan ve başa dönen destan.',
      'Cila: çağ geçişinde zemin daha tok beliriyor, adanın üstünde hafif bir rüzgâr esiyor, çağ yazıları daha okunur.',
    ],
    itemsEn: [
      'Combat now reads: the wave panel shows enemy power beside yours with a win/lose mark (✓ ⚠ ✕). You can finally see why a fight goes the way it does.',
      'Every age gets a story: reaching a new age shows its narrative beat, the saga from the dust of the steppe to the stars and back.',
      'Polish: the ground transition pops harder on an age change, a soft wind drifts across the island, and the age text is more legible.',
    ],
  },
  {
    version: '0.13.0',
    items: [
      'Yetenek ağacı artık gerçek bir ağaç: yetenekler dallar hâlinde birbirine bağlı. Bir yeteneğin kilidi, ondan önceki alınınca açılıyor. Bir düğüme dokun, altındaki kartta al.',
      'Düşman görünümü çağınla uyumlu: erken çağlarda yaya akıncılar, ileri çağlarda savaş makineleri. Artık bozkır çağında modern zırhlılarla karşılaşmayacaksın.',
    ],
    itemsEn: [
      'The skill tree is now an actual tree: skills branch off each other, and each one unlocks only after you buy the one before it. Tap a node, then buy it on the card below.',
      'Enemy looks now match your age: early ages face foot raiders, later ages face war machines. No more modern armor showing up in the steppe age.',
    ],
  },
  {
    version: '0.12.0',
    items: [
      'Ganimet artık çağa bağlı: kapının önünde dalga sağmak para etmiyor, yeni çağlara ilerlemek ediyor. Boss hâlâ iki kat ödüyor.',
      'Bilgelik puanları artık çağ atlamayı ödüllendiriyor: kapı kırmak, dalga saymaktan daha değerli.',
      'Eser bedelleri bulunduğun çağa göre: erken kapıdan düşen eseri yakında kullanabilirsin, derin kapılardaki eser gerçek bir yatırım. Bekletmek pahalandırır.',
      'Sinerji savunmaları törpülendi: üç yönü karıştırıp savunmayı bedavaya katlamak artık yok. Kale (iki ordu yolu) hâlâ en sert kapı kalkanı.',
      'Çevrimdışı üretim artık çağ ilerlemesine de sayılıyor: dönmen gerektiğinde kapı seni bekliyor olabilir.',
    ],
    itemsEn: [
      'Spoils now follow the age: milking waves at the gate pays little, advancing pays. The boss still pays double.',
      'Wisdom points now reward advancing ages: breaking a gate is worth more than counting waves.',
      'Artifact costs scale with your age: an early-gate drop is usable soon, a deep-gate drop is a real investment. Hoarding makes it pricier.',
      'Synergy defenses trimmed: mixing all three paths no longer doubles your defense for free. Fortress (two military paths) remains the strongest gate shield.',
      'Offline production now counts toward age progress too: the gate may be waiting when you return.',
    ],
  },
  {
    version: '0.11.3',
    items: [
      'Öldüğünde artık nedenini görüyorsun: çağ bossuna yenilirsen ölüm ekranı bunu söylüyor ve nasıl güçleneceğini hatırlatıyor.',
      'İlk çağ ilerlemesi artık yanlışlıkla boss uyarısı göstermiyor; kapı bildirimi ödülü ve riski açıkça anlatıyor.',
      'Bir avuç sayı ve metin düzeltmesi: çağ numaraları artık her yerde aynı sayılıyor, eser ipucu yeni boss düzenini anlatıyor.',
    ],
    itemsEn: [
      'When you fall you now see why: if the era boss broke your gate, the death screen says so and hints how to grow stronger.',
      'The first age no longer shows a false boss warning; the gate alert now states the reward and the stakes.',
      'A handful of number and text fixes: age numbers now match everywhere, and the artifact hint teaches the new boss model.',
    ],
  },
  {
    version: '0.11.2',
    items: [
      'Yeni oyuncuya nefes alacak alan: ilk birkaç dalga artık daha yumuşak, böylece açılışta ekonomi ya da bilgi kartını seçmek seni 2. dakikada elemiyor. Dördüncü dalgadan sonra tam zorluk geri geliyor, çağ bosslari hiç yumuşamıyor.',
    ],
    itemsEn: [
      'Room to breathe for new players: the first few waves are softer, so opening with an economy or knowledge card no longer ends you at minute two. Full difficulty returns by wave four, and era bosses are never softened.',
    ],
  },
  {
    version: '0.11.1',
    items: [
      'Denge sertleşti: ganimet artık daha zor kazanılıyor ve savunma teknolojileri belirgin pahalandı. Savunmayı ucuza katlayıp bütün tehdidi ezmek yok artık.',
      'Çağlar daha çok ısırıyor: bir çağda oyalandıkça raiderlar seni daha hızlı öğütüyor. Ölmek ve yetenek ağacından güçlenmek yeniden anlamlı.',
    ],
    itemsEn: [
      'Balance tightened: spoils are earned more slowly and defense technologies cost far more. No more doubling your defense for pocket change.',
      'Ages bite harder: lingering in an age lets the raiders grind you down faster. Dying and growing through the skill tree matters again.',
    ],
  },
  {
    version: '0.11.0',
    items: [
      'Dalgalar artık çağa bağlı: her çağ kendi düşman gücünü getiriyor, bir çağda oyalanırsan raiderlar seni yavaşça öğütüyor.',
      'Çağ atlamak artık bedava değil: kaynak eşiğini geçince o çağın BOSS\'u kapıya dayanıyor. Onu kesersen çağ atlar ve kart seçersin; kaybedersen medeniyet düşer.',
      'İlk çağ atlaması hediye (öğretici), ama ondan sonra her çağın kapısında bir era bossu var. Fethetmek için 6 çağ kapısını aşman gerekiyor: artık ilk koşuda değil, bilgelikle.',
      'Kapıya toplanan boss ayrı bir olay: daha uzun bir uyarı, kararan gök, "kapıdaki düşman" bildirimi. Hazır ol.',
    ],
    itemsEn: [
      'Waves are now tied to the age: each age brings its own enemy strength, and lingering in an age lets the raiders slowly grind you down.',
      'Advancing an age is no longer free: crossing the resource threshold summons that era\'s BOSS to the gate. Defeat it to advance and pick a card; lose and your civilization falls.',
      'The first advance is a gift (onboarding), but every age after has an era boss at its gate. Conquest means breaking 6 age gates: not on your first run, but with wisdom.',
      'The boss massing at the gate is its own event: a longer warning, a darkening sky, an "enemy at the gate" alert. Be ready.',
    ],
  },
  {
    version: '0.10.2',
    items: [
      'Boss merdiveni sertleşti: her boss bir öncekinden daha güçlü, 15. dalgadaki fetih bekçisi gerçek bir duvar. Gezegeni ilk koşuda değil, bilgelikle fethedeceksin.',
    ],
  },
  {
    version: '0.10.1',
    items: ['Tempo yavaşlatıldı: oyun artık acele etmiyor. İlk kart ~2 dakikada, ilk boss ~7 dakikada; her karar daha çok nefes alıyor.'],
  },
  {
    version: '0.10.0',
    items: [
      'Felaketler geldi: medeniyetin başına artık sadece ordular değil, depremler, vebalar, yangınlar, piyasa çöküşleri ve isyanlar da gelebiliyor. Tamamen rastgele, ilk dalgalarda asla.',
      'Dalga sayacını beklemek zorunda değilsin: Dalgayı şimdi gönder butonuyla savaşı öne çekebilirsin.',
      'Medeniyeti bitir: rekor çağına ulaştıysan koşuyu kendi kararınla kapatıp bilgelik puanlarını alabilir, yetenek ağacından güçlenip yeniden başlayabilirsin.',
    ],
  },
  {
    version: '0.9.2',
    items: ['Telefon düzeni yenilendi: paneller artık üst üste binmiyor, harita ekranı dolduruyor.'],
  },
  {
    version: '0.9.0',
    items: [
      'Dünya canlandı: ebedi mavi gök artık ufukta parlıyor ve her çağda değişiyor (uzay çağında yıldızlı gökyüzü).',
      'Zemin çağla birlikte dönüşüyor: kuru altın bozkırdan mor regolite; çiçekler, çakıllar, kristaller ve adanın toprak kenarı.',
      'Köyden ufka uzanan yollar: yerleşim artık dört mahalle halinde büyüyor.',
      'Çağ atlamak artık bir tören: gök değişir, zemin dalga dalga yenilenir, kale yeni çağ formuyla yükselir.',
      'Bacalardan duman tütüyor, yürüyen ordular toz kaldırıyor, boss gelirken dünya kararıyor ve dev makine ağır adımlarla en son ulaşıyor.',
      'Ve... dalgayı tutarsan bozkırda bir bozkurt belirebilir. Göğe uluyor. 🐺',
    ],
  },
  {
    version: '0.8.2',
    items: ['Google ile giriş geldi: artık tek dokunuşla oynayabilirsin. Daha önce e-postayla girdiysen aynı hesabın ve kaydın açılır.'],
  },
  {
    version: '0.8.0',
    items: [
      'BÜYÜK DENGE: ilk boss artık yenilebilir (hazırlanırsan). Büyümek seni cezalandırmıyor, ekonomi/bilgi kartı seçmek tuzak değil.',
      'Ölüm artık kazanç: her düşüş çok daha fazla yetenek puanı verir (ilk boss ölümü 7-9 puan) ve 2. koşu gözle görülür güçlü başlar.',
      'Boss telegrafı: dalga panelinde 💀 boss sayacı + boss öncesi hazırlan uyarısı. Ganimet ekonomisi ve Siper (Bulwark) buna göre ayarlandı.',
      'Her koşuda daha çok kart: çağlar daha sık gelir, sinerjiler artık ulaşılabilir.',
      'Görsel: dev boss makineleri, birimlere gölge, daha görünür işçiler; telefonlarda daha akıcı performans; 5 dilde eksiksiz çeviri.',
    ],
  },
  {
    version: '0.7.9',
    items: [
      'Oto-toplama artık canlı: köyden işçiler çıkıp en yakın kaynağa gidip topluyor ve getiriyor. Getirdikleri miktar gerçek toplama oranını yansıtır (uzak ve değerli kaynak daha uzun sürer).',
      'Çağa göre taşıyıcı: yaya → at arabası → kamyon; modern çağda daha hızlı ve daha büyük yük.',
      'Oduncu, taş işçisi ve madenci kaynak rengine göre farklı görünür.',
    ],
  },
  {
    version: '0.7.8',
    items: [
      'Çeviriler tamam: kartlar, yetenekler, teknolojiler, eserler ve binalar artık 5 dilde.',
      'Çağ savaş makineleri: iri düşman ve boss çağa göre koçbaşı → mancınık → top → tank → füze.',
      'Güzelleştirme: binalarda hafif renk çeşitliliği, evlerde aynalama ve sıcak pencere ışıkları.',
    ],
  },
  {
    version: '0.7.7',
    items: [
      'Çağ şekilleri TAMAMLANDI: artık tüm binalar (evler, toplayıcılar, kışla, okul ve şehir merkezi) her çağda farklı silüet alıyor.',
      'Savaş birimleri çağa göre silah taşıyor: mızrak → kılıç → kargı → tüfek → lazer.',
      'Şehir merkezi çağ atlayınca yenileniyor; mahalle çeşitliliği için evler aynalanıyor.',
    ],
  },
  {
    version: '0.7.6',
    items: [
      'Çağ şekilleri (büyük adım, 1. parça): EVLER artık her çağda farklı ŞEKİL alıyor, yurt (bozkır) → ahşap kabin → taş kubbeli ev → modern blok → uzay kapsülü.',
    ],
  },
  {
    version: '0.7.5',
    items: [
      'Çağ görselleri: her bina ve savaş birimi inşa edildiği çağın rengini alıyor. Çağ atladıkça yeni yapılar yeni çağ tonunda eskinin üstüne yükseliyor (bozkır → imparatorluk → modern → uzay).',
      'Çağ atlamak artık daha çok kaynak istiyor.',
    ],
  },
  {
    version: '0.7.4',
    items: [
      'Mobil hasat: dokun-yapıştır. Bir yere dokun, sarı daire orada toplar; başka yere dokununca taşınır. Artık parmağı basılı tutup sürüklemen gerekmiyor.',
      'Tutorial mobil için iyileştirildi: dokunma ipucu (👆) + ilk hasatta otomatik ilerler.',
    ],
  },
  {
    version: '0.7.3',
    items: [
      'Denge: askeri yatırım yapmazsan dalgalar artık daha zor. Pasif savunma (taban + otomatik kışla + bilgi) seni tek başına taşımıyor; ordu kartları asıl savunma.',
    ],
  },
  {
    version: '0.7.2',
    items: ['En alttaki sürüm artık bir buton (📋); dokununca changelog açılır.'],
  },
  {
    version: '0.7.0',
    items: [
      'Dil desteği: İngilizce (varsayılan), Türkçe, İspanyolca, Almanca, Fransızca.',
      'Dili 👤 hesap panelinden değiştirebilirsin.',
    ],
  },
  {
    version: '0.6.8',
    items: [
      'Yeni oyuncu rehberi (tutorial) eklendi.',
      'Rehberi istediğin zaman 👤 hesap panelinden tekrar açabilirsin.',
    ],
  },
  {
    version: '0.6.7',
    items: [
      'Savaş sayıları artık birimlerin üstünden uçuşuyor (Diablo tarzı).',
      'Bina sayısı artık nüfusla orantılı: kişi başı en çok bir bina.',
      'Sol üstteki panelde sonraki bina için gereken kaynaklar bar olarak görünüyor.',
      'Güncellemeler artık otomatik gelir (sayfayı yenilemeye gerek yok).',
    ],
  },
  {
    version: '0.6.6',
    items: [
      'Girişte "Yenilikler" ekranı: son ziyaretinden beri değişenleri gösterir.',
      'Arada bir kısa geri bildirim hatırlatması.',
    ],
  },
  {
    version: '0.6.5',
    items: ['Savaş kaydı eklendi: dövüş sırasında hasar / savuşturma / kalkan satırları akıyor.'],
  },
  {
    version: '0.6.4',
    items: ['Savaş bitince birimler düzgün geri çekiliyor (sahada takılmıyor).'],
  },
  {
    version: '0.6.3',
    items: [
      'Savaşlar artık açık alanda görünür ve birkaç saniye sürüyor.',
      'Dalga panelinde sadece kendi gücün ve sayaç var.',
    ],
  },
  {
    version: '0.6.2',
    items: [
      'Kaynak toplama daha tok ve daha yavaş.',
      'Otomatik toplama, toplayıcı binaları kurunca açılıyor.',
    ],
  },
  { version: '0.6.1', items: ['Profil panelinden tüm ilerlemeyi sıfırlama eklendi.'] },
  { version: '0.6.0', items: ['E-posta ile giriş, bulut kayıt ve oyun içi geri bildirim.'] },
  {
    version: '0.5.0',
    items: [
      'Göktürk teması: çağlar, yerleşim kademeleri, gök isimleri.',
      'Pixel art dünya, geniş harita, çeşit çeşit binalar.',
    ],
  },
];

/** Returns the news entries newer than the player's last-seen version. */
export function newsSince(lastSeen: string | null): NewsEntry[] {
  if (!lastSeen) return [];
  const idx = NEWS.findIndex((n) => n.version === lastSeen);
  return idx === -1 ? NEWS.slice(0, 3) : NEWS.slice(0, idx);
}
